import { useMemo, useState } from 'react';
import { Navigate, useLocation, useNavigate, useParams } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { ChevronLeft, MessageCircleOff, MessagesSquare, Phone, Send, TriangleAlert } from 'lucide-react';
import { disqualifyDeal, moveDeal, type CardResponse, type CardStage, type LostReason } from '@/api/crmCard';
import { getDeal } from '@/api/pipeline';
import { EmptyState } from '@/components/EmptyState';
import { Sheet } from '@/components/Sheet';
import { CallOverlay } from '@/components/chat/CallOverlay';
import { DealCardBody } from '@/components/chat/DealSheet';
import { QuickTaskSheet } from '@/components/chat/QuickTaskSheet';
import { StageSheet } from '@/components/chat/StageSheet';
import { useCallFlow } from '@/hooks/useCallFlow';
import { ApiError } from '@/lib/http';
import { personName } from '@/lib/dealCard';
import { useProjectStore } from '@/store/project';
import { useToast } from '@/store/toast';

/** Ссылка «написать в WhatsApp» по номеру: только цифры, без плюса. */
export function whatsappLink(phone: string | null | undefined): string | null {
  const digits = String(phone ?? '').replace(/\D+/g, '');
  return digits.length >= 7 ? `https://wa.me/${digits}` : null;
}

/**
 * Отдельный экран сделки (этап 3) — для сделки без переписки: та же карточка, что над перепиской
 * (DealCardBody из этапа 2b), и блок «Переписки нет» с «Позвонить» / «Написать». Сделку с перепиской
 * «Воронка» открывает сразу в переписке, но и сюда по прямой ссылке она попадёт — тогда кнопка ведёт
 * в переписку.
 */
export default function DealPage() {
  const { dealId } = useParams();
  if (!dealId) return <Navigate to="/pipeline" replace />;
  return <DealView dealId={dealId} />;
}

function DealView({ dealId }: { dealId: string }) {
  const qc = useQueryClient();
  const navigate = useNavigate();
  const location = useLocation();
  const toast = useToast((t) => t.show);
  const project = useProjectStore((s) => s.activeProjectId);
  const [sheet, setSheet] = useState<null | 'stage' | 'task'>(null);
  const key = ['crm', 'deal', project, dealId] as const;
  const dealQ = useQuery({ queryKey: key, queryFn: () => getDeal(dealId), staleTime: 15_000, retry: (n, e) => !(e instanceof ApiError && e.status === 404) && n < 2 });
  const deal = dealQ.data;

  const card: CardResponse | undefined = useMemo(() => {
    if (!deal) return undefined;
    const stages = deal.pipeline?.stages ?? [];
    return {
      found: true,
      person: deal.person ?? null,
      stages,
      deal: { ...deal, pipeline: deal.pipeline ? { id: deal.pipeline.id, name: deal.pipeline.name } : null },
    };
  }, [deal]);

  const name = deal ? (deal.company?.name || personName(deal.person) || deal.title) : '';
  const phone = deal?.person?.phones?.[0] ?? null;
  const wa = whatsappLink(phone);
  const call = useCallFlow({ dealId: deal?.id, personId: deal?.person?.id, name });
  const conv = deal?.chatwootAccountId && deal.chatwootDisplayId
    ? { accountId: deal.chatwootAccountId, displayId: deal.chatwootDisplayId }
    : null;

  const back = () => {
    if (location.key !== 'default') navigate(-1);
    else navigate('/pipeline');
  };
  const changed = () => {
    void qc.invalidateQueries({ queryKey: key });
    void qc.invalidateQueries({ queryKey: ['crm', 'deals', project] });
    void qc.invalidateQueries({ queryKey: ['crm', 'deals-count', project] });
  };
  const write = () => {
    if (wa) window.open(wa, '_blank', 'noopener');
    else toast('У клиента не указан номер телефона');
  };

  const pick = async (st: CardStage) => {
    setSheet(null);
    if (!deal) return;
    try {
      await moveDeal(deal.id, st.id);
      toast(`Стадия изменена: ${st.name}`);
    } catch (e) {
      toast(e instanceof Error ? e.message : 'Не удалось сменить стадию', 5000);
    }
    changed();
  };
  const lose = async (st: CardStage, reason: LostReason, comment: string) => {
    setSheet(null);
    if (!deal) return;
    try {
      await disqualifyDeal(deal.id, { reasonId: reason.id, ...(comment ? { comment } : {}), stageId: st.id });
      toast('Сделка закрыта с отказом');
    } catch (e) {
      toast(e instanceof Error ? e.message : 'Не удалось закрыть сделку', 5000);
    }
    changed();
  };

  let body: React.ReactNode;
  if (dealQ.isPending) {
    body = <div className="scr flex items-center justify-center" role="status" aria-label="Загрузка сделки"><span className="spinner size-9" /></div>;
  } else if (dealQ.isError || !card) {
    const notFound = dealQ.error instanceof ApiError && dealQ.error.status === 404;
    body = (
      <div className="scr">
        <EmptyState
          icon={notFound ? MessageCircleOff : TriangleAlert}
          title={notFound ? 'Сделка не найдена' : 'Сделка не загрузилась'}
          text={notFound ? 'Возможно, её удалили, объединили с другой или у вас нет к ней доступа.' : dealQ.error?.message}
          action={notFound ? { label: 'К воронке', onClick: back } : { label: 'Повторить', onClick: () => void dealQ.refetch() }}
        />
      </div>
    );
  } else {
    body = (
      <div className="deal-page">
        <div className="noconv" style={{ marginTop: 10, marginBottom: 6 }}>
          {conv ? (
            <>
              <p><b>Есть переписка с клиентом.</b> Карточка сделки откроется поверх неё.</p>
              <div className="btnrow">
                <button type="button" className="primary" onClick={() => navigate(`/inbox/${conv.accountId}/${conv.displayId}`, { state: { openDeal: true } })}>
                  <MessagesSquare aria-hidden />Открыть переписку
                </button>
              </div>
            </>
          ) : (
            <>
              <p><b>Переписки нет.</b> Когда клиент напишет в любой канал, переписка появится во «Входящих». Пока можно позвонить или написать первым.</p>
              <div className="btnrow">
                <button type="button" className="primary" disabled={call.starting} onClick={() => void call.start(phone)}><Phone aria-hidden />Позвонить</button>
                <button type="button" className="ghost" onClick={write}><Send aria-hidden />Написать</button>
              </div>
            </>
          )}
        </div>
        <DealCardBody
          project={project}
          card={card}
          contactName={personName(deal?.person) || name}
          contactPhone={phone}
          onStage={() => setSheet('stage')}
          onCall={() => void call.start(phone)}
          onTask={() => setSheet('task')}
          onMeeting={() => toast('Встречи появятся на следующем этапе (этап 4)')}
          chatAction={conv
            ? { label: 'Чат', icon: MessagesSquare, onClick: () => navigate(`/inbox/${conv.accountId}/${conv.displayId}`) }
            : { label: 'Написать', icon: Send, onClick: write }}
        />
      </div>
    );
  }

  return (
    <div className="conv-layer" role="region" aria-label={name ? `Сделка ${name}` : 'Сделка'}>
      <div className="chdr">
        <button type="button" className="ibtn" onClick={back} aria-label="Назад"><ChevronLeft aria-hidden /></button>
        <span className="ct"><b>{name || 'Сделка'}</b><span>{deal?.pipeline?.name ?? ''}</span></span>
      </div>
      {body}

      {sheet === 'stage' && deal && card && (
        <StageSheet
          project={project}
          pipelineName={deal.pipeline?.name ?? ''}
          stages={card.stages ?? []}
          currentId={deal.stageId}
          onPick={(st) => void pick(st)}
          onLost={(st, reason, comment) => void lose(st, reason, comment)}
          onClose={() => setSheet(null)}
        />
      )}
      {sheet === 'task' && deal && (
        <QuickTaskSheet project={project} dealId={deal.id} pipelineId={deal.pipelineId} onClose={() => { setSheet(null); changed(); }} />
      )}
      {call.notice && (
        <Sheet label="Звонок" onClose={call.closeNotice}>
          <div className="sh"><h3>Позвонить</h3></div>
          <div className="sb">
            <p className="pnote" style={{ fontSize: 15, color: 'var(--fg)' }}>{call.notice.text}</p>
            <div className="btnrow">
              {call.notice.tel && <a className="primary" href={call.notice.tel} onClick={call.closeNotice}>Позвонить с телефона</a>}
              <button type="button" className="ghost" onClick={call.closeNotice}>Понятно</button>
            </div>
          </div>
        </Sheet>
      )}
      {call.ui && <CallOverlay call={call.ui} seed={hashSeed(dealId)} onClose={call.close} />}
    </div>
  );
}

/** Число для цвета аватара из идентификатора сделки. */
function hashSeed(id: string): number {
  let h = 0;
  for (let i = 0; i < id.length; i++) h = (h * 31 + id.charCodeAt(i)) | 0;
  return Math.abs(h);
}
